/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import React from 'react';
import { IScopeAncestry, isInScope, minimizeExclusions } from '../../../../../common/src/domain/surveyScopeExclusion/scopeResolver';
import { SurveyScopeEntityType, SurveyScopeExclusion } from '../../../../../common/src/domain/surveyScopeExclusion/surveyScopeExclusionTableSchema';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchJson } from '../../../store/fetch';
import { Spinner } from '../../spinners';
import { ICategory } from '../Interfaces/ICategory';
import { IElement } from '../Interfaces/IElement';

export interface IScopePickerExclusionInput {
    entityType: SurveyScopeEntityType;
    entityId: string;
}

export interface IDecompositionTree {
    categories: ICategory[];
    elements: IElement[];
}

interface IScopePickerProps {
    surveyId?: string | undefined;
    objectId?: string | undefined;
    decomposition?: IDecompositionTree | undefined;
    exclusions: SurveyScopeExclusion[] | IScopePickerExclusionInput[];
    onChange: (exclusions: IScopePickerExclusionInput[]) => void;
}

type AnyExclusion = SurveyScopeExclusion | IScopePickerExclusionInput;

function toAncestry(
    entityType: SurveyScopeEntityType,
    ids: { elementCategoryId?: string; elementId?: string; unitId?: string; manifestationId?: string },
): IScopeAncestry {
    return {
        elementCategoryId: ids.elementCategoryId ?? null,
        elementId: entityType === 'element' || entityType === 'unit' || entityType === 'manifestation' ? (ids.elementId ?? null) : null,
        unitId: entityType === 'unit' || entityType === 'manifestation' ? (ids.unitId ?? null) : null,
        manifestationId: entityType === 'manifestation' ? (ids.manifestationId ?? null) : null,
    };
}

function asInputs(exclusions: AnyExclusion[]): IScopePickerExclusionInput[] {
    return exclusions.map((e) => ({ entityType: e.entityType, entityId: e.entityId }));
}

function buildAncestorLookup(decomposition: IDecompositionTree) {
    const lookup = new Map<string, IScopeAncestry>();
    for (const element of decomposition.elements) {
        const elementAncestry: IScopeAncestry = {
            elementCategoryId: element.categoryId ?? null,
            elementId: element.id,
            unitId: null,
            manifestationId: null,
        };
        lookup.set(`element:${element.id}`, elementAncestry);
        for (const unit of element.units) {
            const unitAncestry: IScopeAncestry = {
                elementCategoryId: element.categoryId ?? null,
                elementId: element.id,
                unitId: unit.id,
                manifestationId: null,
            };
            lookup.set(`unit:${unit.id}`, unitAncestry);
            for (const manifestation of unit.manifestations) {
                const manifestationAncestry: IScopeAncestry = {
                    elementCategoryId: element.categoryId ?? null,
                    elementId: element.id,
                    unitId: unit.id,
                    manifestationId: manifestation.id,
                };
                lookup.set(`manifestation:${manifestation.id}`, manifestationAncestry);
            }
        }
    }
    for (const category of decomposition.categories) {
        lookup.set(`elementCategory:${category.id}`, {
            elementCategoryId: category.id,
            elementId: null,
            unitId: null,
            manifestationId: null,
        });
    }
    return (entityType: SurveyScopeEntityType, entityId: string): IScopeAncestry => {
        const key = `${entityType}:${entityId}`;
        return lookup.get(key) ?? { elementCategoryId: null, elementId: null, unitId: null, manifestationId: null };
    };
}

function gatherDescendantExclusions(
    entityType: SurveyScopeEntityType,
    entityId: string,
    decomposition: IDecompositionTree,
): IScopePickerExclusionInput[] {
    const result: IScopePickerExclusionInput[] = [];
    if (entityType === 'elementCategory') {
        for (const element of decomposition.elements) {
            if (element.categoryId === entityId) {
                result.push({ entityType: 'element', entityId: element.id });
                for (const unit of element.units) {
                    result.push({ entityType: 'unit', entityId: unit.id });
                    for (const manifestation of unit.manifestations) {
                        result.push({ entityType: 'manifestation', entityId: manifestation.id });
                    }
                }
            }
        }
    } else if (entityType === 'element') {
        const element = decomposition.elements.find((e) => e.id === entityId);
        if (element) {
            for (const unit of element.units) {
                result.push({ entityType: 'unit', entityId: unit.id });
                for (const manifestation of unit.manifestations) {
                    result.push({ entityType: 'manifestation', entityId: manifestation.id });
                }
            }
        }
    } else if (entityType === 'unit') {
        for (const element of decomposition.elements) {
            const unit = element.units.find((u) => u.id === entityId);
            if (unit) {
                for (const manifestation of unit.manifestations) {
                    result.push({ entityType: 'manifestation', entityId: manifestation.id });
                }
                break;
            }
        }
    }
    return result;
}

function isDescendantOf(
    candidateType: SurveyScopeEntityType,
    candidateAncestry: IScopeAncestry,
    ancestorType: SurveyScopeEntityType,
    ancestorId: string,
): boolean {
    if (candidateType === ancestorType) {
        return false;
    }
    if (ancestorType === 'elementCategory') {
        return candidateAncestry.elementCategoryId === ancestorId;
    }
    if (ancestorType === 'element') {
        return candidateAncestry.elementId === ancestorId;
    }
    if (ancestorType === 'unit') {
        return candidateAncestry.unitId === ancestorId;
    }
    return false;
}

export function ScopePicker(props: IScopePickerProps): JSX.Element {
    const translate = useTranslate();
    const [decomposition, setDecomposition] = React.useState<IDecompositionTree | undefined>(props.decomposition);
    const [isLoading, setIsLoading] = React.useState<boolean>(Boolean((props.surveyId || props.objectId) && !props.decomposition));
    const [openMap, setOpenMap] = React.useState<{ [key: string]: boolean }>({});

    const { surveyId: propSurveyId, objectId: propObjectId, decomposition: propDecomposition } = props;
    React.useEffect(() => {
        if (propDecomposition) {
            setDecomposition(propDecomposition);
            setIsLoading(false);
            return undefined;
        }
        // Need at least one anchor to fetch the decomposition.
        if (!propObjectId && !propSurveyId) {
            return undefined;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                // In create-mode we receive objectId directly; in edit-mode we may
                // only have surveyId and must look up the object first.
                let objectId = propObjectId;
                if (!objectId && propSurveyId) {
                    const surveyResult = await fetchJson(`/api/surveys/${propSurveyId}`, 'GET');
                    if (cancelled || !surveyResult.json) {
                        return;
                    }
                    ({ objectId } = surveyResult.json);
                }
                if (!objectId) {
                    return;
                }
                // scopeOnly=true triggers backend strict-filtering:
                //   - With surveyId (edit existing survey): general (surveyId IS NULL) PLUS this-survey-specific
                //     entities — survey-specific items belonging to this survey are still scope-eligible.
                //   - Without surveyId (creating a brand new survey): general only.
                // Either way the LEFT JOIN + WHERE drops elements whose units all belong to OTHER surveys.
                const elementsUrl = propSurveyId
                    ? `/api/objects/${objectId}/elements?scopeOnly=true&surveyId=${propSurveyId}`
                    : `/api/objects/${objectId}/elements?scopeOnly=true`;
                const [categoriesResult, elementsResult] = await Promise.all([
                    fetchJson(`/api/objects/${objectId}/categories`, 'GET'),
                    fetchJson(elementsUrl, 'GET'),
                ]);
                if (cancelled) {
                    return;
                }
                setDecomposition({
                    categories: categoriesResult.json ?? [],
                    elements: elementsResult.json ?? [],
                });
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [propSurveyId, propObjectId, propDecomposition]);

    const currentExclusions: IScopePickerExclusionInput[] = React.useMemo(() => asInputs(props.exclusions as AnyExclusion[]), [props.exclusions]);

    const ancestorLookup = React.useMemo(() => {
        if (!decomposition) {
            return null;
        }
        return buildAncestorLookup(decomposition);
    }, [decomposition]);

    function isEntityInScope(entityType: SurveyScopeEntityType, ids: Parameters<typeof toAncestry>[1]): boolean {
        return isInScope(currentExclusions as SurveyScopeExclusion[], toAncestry(entityType, ids));
    }

    function toggleOpen(key: string) {
        setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    function handleToggle(entityType: SurveyScopeEntityType, entityId: string, isChecked: boolean, ancestryIds: Parameters<typeof toAncestry>[1]) {
        if (!decomposition || !ancestorLookup) {
            return;
        }
        if (isChecked) {
            // user is unchecking -> add exclusion for this node, remove any descendant exclusions (they become redundant)
            const filtered = currentExclusions.filter((e) => {
                const ancestry = ancestorLookup(e.entityType, e.entityId);
                return !isDescendantOf(e.entityType, ancestry, entityType, entityId);
            });
            const next = [...filtered, { entityType, entityId }];
            const minimized = minimizeExclusions(next, ancestorLookup);
            props.onChange(minimized);
        } else {
            // user is checking -> remove exclusion for this node and all descendants currently excluded under it
            // also remove ancestor exclusions covering this node, but then re-exclude siblings that should still be out-of-scope
            const ancestry = toAncestry(entityType, ancestryIds);
            const ancestorMatches: AnyExclusion[] = currentExclusions.filter((e) => {
                if (e.entityType === entityType && e.entityId === entityId) {
                    return true;
                }
                if (e.entityType === 'elementCategory' && ancestry.elementCategoryId === e.entityId) {
                    return true;
                }
                if (e.entityType === 'element' && ancestry.elementId === e.entityId) {
                    return true;
                }
                if (e.entityType === 'unit' && ancestry.unitId === e.entityId) {
                    return true;
                }
                return false;
            });
            // Remove all ancestor & this-node exclusions, plus descendant exclusions
            const filtered = currentExclusions.filter((e) => {
                if (ancestorMatches.some((m) => m.entityType === e.entityType && m.entityId === e.entityId)) {
                    return false;
                }
                const eAncestry = ancestorLookup(e.entityType, e.entityId);
                if (isDescendantOf(e.entityType, eAncestry, entityType, entityId)) {
                    return false;
                }
                return true;
            });

            // For each ancestor that was previously excluded, we need to re-exclude its siblings (subtrees other than the path leading to this entity)
            const reAdded: IScopePickerExclusionInput[] = [];
            for (const ancestor of ancestorMatches) {
                if (ancestor.entityType === entityType && ancestor.entityId === entityId) {
                    continue;
                }
                const descendants = gatherDescendantExclusions(ancestor.entityType, ancestor.entityId, decomposition);
                for (const desc of descendants) {
                    // Skip the descendant that is on the path to the entity being checked
                    const descAncestry = ancestorLookup(desc.entityType, desc.entityId);
                    if (desc.entityType === entityType && desc.entityId === entityId) {
                        continue;
                    }
                    // skip ancestors of the target
                    if (
                        (desc.entityType === 'element' && desc.entityId === ancestry.elementId) ||
                        (desc.entityType === 'unit' && desc.entityId === ancestry.unitId) ||
                        (desc.entityType === 'manifestation' && desc.entityId === ancestry.manifestationId)
                    ) {
                        continue;
                    }
                    // skip descendants of the target entity
                    if (isDescendantOf(desc.entityType, descAncestry, entityType, entityId)) {
                        continue;
                    }
                    reAdded.push(desc);
                }
            }
            const next = [...filtered, ...reAdded];
            const minimized = minimizeExclusions(next, ancestorLookup);
            props.onChange(minimized);
        }
    }

    if (isLoading || !decomposition) {
        return (
            <div className="mt20 text-center">
                <Spinner />
            </div>
        );
    }

    const { categories } = decomposition;
    const elementsByCategory = new Map<string, IElement[]>();
    for (const element of decomposition.elements) {
        const list = elementsByCategory.get(element.categoryId ?? '') ?? [];
        list.push(element);
        elementsByCategory.set(element.categoryId ?? '', list);
    }

    if (categories.length === 0 && decomposition.elements.length === 0) {
        // SurveyAddEdit owns the "select an object first" empty state at a higher level;
        // by the time we render here, an object IS selected — it just has no scope-eligible
        // decomposition (no general elements, or all units belong to other surveys).
        return <div className="p20 text-muted">{translate('survey.scopeNoElements')}</div>;
    }

    function renderCheckbox(entityType: SurveyScopeEntityType, entityId: string, label: string, ancestryIds: Parameters<typeof toAncestry>[1]) {
        const inScope = isEntityInScope(entityType, ancestryIds);
        return (
            <label
                className={classNames('cursor', { inScope, outOfScope: !inScope })}
                style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8, marginBottom: 0 }}
            >
                <input
                    type="checkbox"
                    checked={inScope}
                    onChange={() => handleToggle(entityType, entityId, inScope, ancestryIds)}
                    style={{ marginRight: 5, marginTop: 0 }}
                />
                {label}
            </label>
        );
    }

    // Fixed-width container for the open/close caret so:
    //   - leaf rows (no children → no caret) stay aligned with rows that do have a caret
    //   - swapping fa-caret-right ↔ fa-caret-down on open/close can't shift the checkbox sideways
    //     (the icons render at slightly different widths otherwise)
    function renderToggle(toggleKey: string, hasChildren: boolean) {
        const isOpen = openMap[toggleKey] ?? false;
        return (
            <span style={{ display: 'inline-block', width: 16, marginRight: 4, textAlign: 'center', flexShrink: 0 }}>
                {hasChildren ? (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            toggleOpen(toggleKey);
                        }}
                    >
                        <i className={`fa fa-lg ${isOpen ? 'fa-caret-down' : 'fa-caret-right'}`} />
                    </a>
                ) : null}
            </span>
        );
    }

    return (
        <div id="scope-picker">
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                {categories.length > 0
                    ? categories.map((category) => {
                          const catElements = elementsByCategory.get(category.id) ?? [];
                          const catKey = `cat:${category.id}`;
                          const isOpen = openMap[catKey] ?? false;
                          return (
                              <li key={category.id} style={{ marginBottom: 10 }}>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                      {renderToggle(catKey, true)}
                                      {renderCheckbox('elementCategory', category.id, category.name || '—', { elementCategoryId: category.id })}
                                  </div>
                                  {isOpen && catElements.length > 0 && (
                                      <ul style={{ listStyle: 'none', paddingLeft: 28 }}>
                                          {catElements.map((element) => renderElement(element, category.id))}
                                      </ul>
                                  )}
                              </li>
                          );
                      })
                    : decomposition.elements.map((element) => renderElement(element, element.categoryId ?? ''))}
            </ul>
        </div>
    );

    function renderElement(element: IElement, categoryId: string) {
        const elKey = `el:${element.id}`;
        const isOpen = openMap[elKey] ?? false;
        const hasChildren = element.units.length > 0;
        return (
            <li key={element.id} style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {renderToggle(elKey, hasChildren)}
                    {renderCheckbox('element', element.id, element.name, { elementCategoryId: categoryId, elementId: element.id })}
                </div>
                {isOpen && hasChildren && (
                    <ul style={{ listStyle: 'none', paddingLeft: 28 }}>
                        {element.units.map((unit) => {
                            const unitKey = `unit:${unit.id}`;
                            const isUnitOpen = openMap[unitKey] ?? false;
                            const hasUnitChildren = unit.manifestations.length > 0;
                            return (
                                <li key={unit.id} style={{ marginTop: 2 }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {renderToggle(unitKey, hasUnitChildren)}
                                        {renderCheckbox('unit', unit.id, unit.name, {
                                            elementCategoryId: categoryId,
                                            elementId: element.id,
                                            unitId: unit.id,
                                        })}
                                    </div>
                                    {isUnitOpen && hasUnitChildren && (
                                        <ul style={{ listStyle: 'none', paddingLeft: 28 }}>
                                            {unit.manifestations.map((manifestation) => (
                                                <li key={manifestation.id} style={{ marginTop: 2 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        {renderToggle(`man:${manifestation.id}`, false)}
                                                        {renderCheckbox('manifestation', manifestation.id, manifestation.name, {
                                                            elementCategoryId: categoryId,
                                                            elementId: element.id,
                                                            unitId: unit.id,
                                                            manifestationId: manifestation.id,
                                                        })}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </li>
        );
    }
}
